import * as _ from 'lodash';
import { stringify } from 'query-string';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { SearchParameters } from 'services';
import { ApiModel } from 'shared/models';
import {
    ArticlesForOrderSearchResult,
    ProductSearchErrorType,
    ProductSearchResponse,
    ProductSearchResult,
    ProductSearchWarningType,
    SortingCriteria
} from './searcher.types';

export class Searcher {
    constructor(private apiModel: ApiModel) {}

    public contentAreaSearch(
        {
            query,
            pagination,
            filters: {
                brands,
                seasons,
                targetGroups,
                hasSustainabilityAttributes,
                mainCategory,
                colors,
                orderedArticles
            }
        }: SearchParameters,
        language: string
    ): Observable<ProductSearchResponse> {
        const brandId = _.first(brands);
        if (!brandId) {
            return of({ error: ProductSearchErrorType.MissingBrand });
        }

        const getIfNotEmpty = (filter: string[], key: string) => (mappingFunction) =>
            !_.isEmpty(filter) ? { [key]: _.chain(filter).map(mappingFunction).join('|').value() } : {};

        return this.apiModel
            .makeHttpObservableRequest({
                method: 'GET',
                url: `${this.apiModel.getBrandsEndpoint()}/${brandId}/products`,
                params: {
                    language,
                    ...(query ? { identifier: query } : {}),
                    ...getIfNotEmpty(
                        seasons,
                        'season'
                    )((season: string) => {
                        const [seasonKey, seasonYearKey] = _.split(season, '|');
                        return JSON.stringify({ seasonKey, seasonYearKey });
                    }),
                    ...getIfNotEmpty(
                        targetGroups,
                        'targetAndAgeGroup'
                    )((targetGroup: string) => JSON.stringify({ targetGroupKey: targetGroup })),
                    ...(hasSustainabilityAttributes ? { hasSustainabilityAttributes } : {}),
                    ...(orderedArticles ? { orderedArticles } : {}),
                    ...(mainCategory ? { mainCategory } : {}),
                    ...getIfNotEmpty(colors, 'color')((color: string) => JSON.stringify({ searchColorKey: color })),
                    ...pagination
                }
            })
            .pipe(
                catchError(({ status, data }: { status: number; data: any }) => {
                    const isIdentifierValidationError =
                        status === 400 && _.includes(_.get(data, 'errors[0].field'), 'identifier');
                    if (isIdentifierValidationError) {
                        return of({
                            warning: ProductSearchWarningType.SearchInputValidation,
                            ...new EmptyContentAreaSearchResult()
                        });
                    }
                    if (status === 403) {
                        return of({ error: ProductSearchErrorType.AccessDenied });
                    }
                    return of({ error: ProductSearchErrorType.Unknown });
                })
            );
    }

    public reorderAreaSearch(
        { query, pagination, filters, sortingCriteria }: SearchParameters,
        retailerId: string
    ): Observable<ArticlesForOrderSearchResult> {
        // backend requires the search query to be longer than 2 chars
        const searchQuery = query?.length > 2 ? { q: query } : {};
        const queryParams = {
            ...filters,
            ...sortingCriteria,
            ...searchQuery,
            retailerId,
            ...pagination
        };

        const url = `${this.apiModel.getRetailerSearchEndpoint()}?${stringify(queryParams, {
            arrayFormat: 'bracket',
            sort: false
        })}`;
        return this.apiModel
            .makeHttpObservableRequest({ url, method: 'GET' })
            .pipe(
                catchError(() =>
                    of({ articles: [], count: 0, facets: {}, errorRaised: true } as ArticlesForOrderSearchResult)
                )
            );
    }

    public preorderAreaSearch(
        { query, pagination, filters }: SearchParameters,
        retailerId: string
    ): Observable<ArticlesForOrderSearchResult> {
        // backend requires the search query to be longer than 2 chars
        const searchQuery = query?.length > 2 ? { q: query } : {};
        const queryParams = { ...filters, ...searchQuery, retailerId, ...pagination };

        const url = `${this.apiModel.getPreorderEndpoint()}/search?${stringify(queryParams, {
            arrayFormat: 'bracket',
            sort: false
        })}`;
        return this.apiModel
            .makeHttpObservableRequest({ url, method: 'GET' })
            .pipe(
                catchError(() =>
                    of({ articles: [], count: 0, facets: {}, errorRaised: true } as ArticlesForOrderSearchResult)
                )
            );
    }

    public getSortingCriteria(
        sortingCriteriaList,
        sortingCriteria: { sortBy: string; sortOrder: string }
    ): SortingCriteria {
        if (!sortingCriteria) {
            return undefined;
        }

        return _.find(
            sortingCriteriaList,
            (option) =>
                option.value.sortBy === sortingCriteria.sortBy && option.value.sortOrder === sortingCriteria.sortOrder
        );
    }
}

class EmptyContentAreaSearchResult implements ProductSearchResult {
    public products = [];
    public count = 0;
    public facets = {
        seasons: [],
        targetGroups: [],
        targetAndAgeGroups: [],
        mainCategories: [],
        ageGroups: [],
        colors: [],
        sustainableProducts: [],
        orderedArticles: []
    };
}
